/**
 * Semantic Graph Visualization Component
 *
 * Displays a 2D scatter plot showing the product position relative
 * to common questions, with BEFORE/AFTER visualization showing
 * the improvement from original content to AEO-enhanced content.
 *
 * Story 79: Product Success Story Dashboard
 */

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FileQuestion, Target } from 'lucide-react';

interface SemanticQuestion {
  id: string;
  text: string;
  position: { x: number; y: number };
  similarity: number;
  originalDistance?: number;   // Distance to best original content chunk
  aeoDistance?: number;        // Distance to best AEO content chunk
  bestDistance?: number;       // Minimum of both (used for positioning)
  status: 'answered' | 'partial' | 'gap';
  searchVolume: number | null;
  cpc: number | null;
  opportunityScore: number | null;
  answerAuthority: string | null;
  category: string | null;
}

interface SemanticGraphProps {
  productName: string;
  questions: SemanticQuestion[];
  summary?: {
    answered: number;
    partial: number;
    gap: number;
    answeredPercent: number;
    partialPercent: number;
    gapPercent: number;
    coveredPercent?: number;  // answered + 50% of partial (more generous)
    searchVolume?: {
      answered: number;
      partial: number;
      gap: number;
      total: number;
      answeredPercent: number;
      coveredPercent?: number;  // More generous coverage metric
    };
    monthlyValue?: {
      answered: number;
      partial: number;
      gap: number;
      total: number;
    };
    aeoImprovement?: {
      improvedCount: number;
      improvedPercent: number;
      avgImprovement: number;           // Average improvement in percentage points
      avgWeightedImprovement: number;   // Weighted by search volume
      totalSearchVolumeImproved: number;
    };
  };
}

const STATUS_LABELS = {
  answered: 'Answered',
  partial: 'Partial',
  gap: 'Gap',
};

// Convert distance (0-1) to radius percentage (10-45%)
// Lower distance = closer to center
function distanceToRadius(distance: number): number {
  const minRadius = 10;
  const maxRadius = 45;
  return minRadius + (distance * (maxRadius - minRadius));
}

// Calculate dot size based on search volume (min 6px, max 16px)
function getDotSize(searchVolume: number | null, maxVolume: number): number {
  if (!searchVolume || searchVolume <= 0 || maxVolume <= 0) return 8; // Default size
  const ratio = Math.min(searchVolume / maxVolume, 1);
  // Scale from 6px to 16px based on search volume
  return 6 + ratio * 10;
}

export function SemanticGraph({ productName, questions, summary }: SemanticGraphProps) {
  // Find max search volume for scaling
  const maxSearchVolume = useMemo(() => {
    if (!questions || questions.length === 0) return 0;
    return Math.max(...questions.map(q => q.searchVolume ?? 0));
  }, [questions]);

  // Compute positions for each question showing BOTH original and AEO positions
  const positionedQuestions = useMemo(() => {
    if (!questions || questions.length === 0) return [];

    return questions.map((q, i) => {
      // Distribute questions around the circle
      const angle = (2 * Math.PI * i) / questions.length;

      // Original position (based on originalDistance)
      const origDist = q.originalDistance ?? 0.7;
      const origRadius = distanceToRadius(origDist);
      const origX = 50 + Math.cos(angle) * origRadius;
      const origY = 50 + Math.sin(angle) * origRadius;

      // AEO position (based on aeoDistance) - same angle, different radius
      const aeoDist = q.aeoDistance ?? origDist;
      const aeoRadius = distanceToRadius(aeoDist);
      const aeoX = 50 + Math.cos(angle) * aeoRadius;
      const aeoY = 50 + Math.sin(angle) * aeoRadius;

      // Has AEO improvement?
      const hasAeoImprovement = aeoDist < origDist && q.aeoDistance != null && q.aeoDistance < 1;

      // Dot size based on search volume
      const dotSize = getDotSize(q.searchVolume, maxSearchVolume);

      return {
        ...q,
        angle,
        origX,
        origY,
        aeoX,
        aeoY,
        hasAeoImprovement,
        dotSize,
      };
    });
  }, [questions, maxSearchVolume]);

  if (!questions || questions.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        <div className="text-center">
          <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">No semantic data available</p>
          <p className="text-sm mb-4">Questions will appear here once embeddings are computed</p>
          <Button variant="outline" asChild>
            <Link to="/demand-research">
              <FileQuestion className="h-4 w-4 mr-2" />
              Generate Demand FAQs
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-sm">Answered ({summary?.answered ?? 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-sm">Partial ({summary?.partial ?? 0})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-sm">Gap ({summary?.gap ?? 0})</span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-blue-400 border border-blue-600" />
            Original
          </span>
          <span className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-purple-500" />
            + AEO
          </span>
          <span className="text-muted-foreground">• Closer = better</span>
          <span className="flex items-center gap-1 text-muted-foreground">
            • Size =
            <span className="inline-flex items-center gap-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
              <div className="w-2.5 h-2.5 rounded-full bg-gray-400" />
              <div className="w-3.5 h-3.5 rounded-full bg-gray-400" />
            </span>
            volume
          </span>
        </div>
      </div>

      {/* Graph container */}
      <TooltipProvider delayDuration={0}>
        <div className="flex justify-center">
          <div
            className="relative bg-muted/30 rounded-lg border"
            style={{ width: '500px', height: '500px' }}
          >
            {/* SVG for lines */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox="0 0 100 100"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Reference circles */}
              <circle cx="50" cy="50" r="17.5" fill="none" stroke="rgba(34, 197, 94, 0.2)" strokeDasharray="2,2" />
              <circle cx="50" cy="50" r="30" fill="none" stroke="rgba(234, 179, 8, 0.2)" strokeDasharray="2,2" />
              <circle cx="50" cy="50" r="42.5" fill="none" stroke="rgba(239, 68, 68, 0.2)" strokeDasharray="2,2" />

              {/* Lines for each question */}
              {positionedQuestions.map((q) => (
                <g key={`lines-${q.id}`}>
                  {/* Line from center to original position */}
                  <line
                    x1="50"
                    y1="50"
                    x2={q.origX}
                    y2={q.origY}
                    stroke="rgba(96, 165, 250, 0.3)"
                    strokeWidth="0.3"
                  />
                  {/* If AEO improvement, line from original to AEO position */}
                  {q.hasAeoImprovement && (
                    <>
                      <line
                        x1={q.origX}
                        y1={q.origY}
                        x2={q.aeoX}
                        y2={q.aeoY}
                        stroke="rgba(168, 85, 247, 0.6)"
                        strokeWidth="0.5"
                        markerEnd="url(#arrowhead)"
                      />
                      {/* Line from center to AEO position */}
                      <line
                        x1="50"
                        y1="50"
                        x2={q.aeoX}
                        y2={q.aeoY}
                        stroke="rgba(168, 85, 247, 0.2)"
                        strokeWidth="0.3"
                        strokeDasharray="1,1"
                      />
                    </>
                  )}
                </g>
              ))}

              {/* Arrow marker definition */}
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="4"
                  markerHeight="4"
                  refX="3"
                  refY="2"
                  orient="auto"
                >
                  <polygon points="0 0, 4 2, 0 4" fill="rgba(168, 85, 247, 0.8)" />
                </marker>
              </defs>
            </svg>

            {/* Product center marker */}
            <div
              className="absolute w-6 h-6 bg-blue-600 rounded-full border-2 border-white shadow-lg flex items-center justify-center z-20"
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <span className="text-white text-[10px] font-bold">P</span>
            </div>

            {/* Product label */}
            <div
              className="absolute bg-blue-600 text-white px-2 py-0.5 rounded text-[10px] font-medium shadow-md z-20 whitespace-nowrap max-w-[200px] truncate"
              style={{
                left: '50%',
                top: 'calc(50% + 18px)',
                transform: 'translateX(-50%)',
              }}
            >
              {productName}
            </div>

            {/* Question dots */}
            {positionedQuestions.map((q) => (
              <React.Fragment key={q.id}>
                {/* Original position dot (blue, hollow if has AEO improvement) */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={`absolute rounded-full cursor-pointer transition-all hover:scale-150 hover:z-30 z-10 focus:outline-none ${
                        q.hasAeoImprovement
                          ? 'bg-blue-200 border border-blue-500'
                          : q.status === 'answered'
                          ? 'bg-green-500'
                          : q.status === 'partial'
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      }`}
                      style={{
                        left: `${q.origX}%`,
                        top: `${q.origY}%`,
                        width: `${q.dotSize}px`,
                        height: `${q.dotSize}px`,
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs z-50">
                    <div className="space-y-1.5">
                      <p className="font-medium text-xs leading-tight">{q.text}</p>
                      <div className="flex items-center gap-1.5 text-[10px] flex-wrap">
                        <Badge
                          variant="outline"
                          className={`text-[10px] px-1 py-0 ${
                            q.status === 'answered' ? 'border-green-500 text-green-600' :
                            q.status === 'partial' ? 'border-yellow-500 text-yellow-600' :
                            'border-red-500 text-red-600'
                          }`}
                        >
                          {STATUS_LABELS[q.status]}
                        </Badge>
                        {q.answerAuthority && (
                          <Badge variant="secondary" className="text-[10px] px-1 py-0">
                            {q.answerAuthority === 'can_answer' || q.answerAuthority === 'product_data'
                              ? 'Can Answer'
                              : 'Research'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] border-t pt-1">
                        <span className="text-blue-600">
                          📄 {((q.originalDistance ?? 0) * 100).toFixed(0)}%
                        </span>
                        {q.hasAeoImprovement && (
                          <span className="text-purple-600">
                            → ✨ {((q.aeoDistance ?? 0) * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {(q.searchVolume ?? 0) > 0 && (
                        <div className="text-[10px] text-muted-foreground">
                          🔍 {q.searchVolume?.toLocaleString()}/mo
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>

                {/* AEO position dot (purple, only if has improvement) */}
                {q.hasAeoImprovement && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className={`absolute rounded-full cursor-pointer transition-all hover:scale-150 hover:z-30 z-10 focus:outline-none ${
                          q.status === 'answered'
                            ? 'bg-green-500 ring-2 ring-purple-400'
                            : q.status === 'partial'
                            ? 'bg-yellow-500 ring-2 ring-purple-400'
                            : 'bg-red-500 ring-2 ring-purple-400'
                        }`}
                        style={{
                          left: `${q.aeoX}%`,
                          top: `${q.aeoY}%`,
                          width: `${q.dotSize}px`,
                          height: `${q.dotSize}px`,
                          transform: 'translate(-50%, -50%)',
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs z-50">
                      <div className="space-y-1.5">
                        <p className="font-medium text-xs leading-tight">{q.text}</p>
                        <div className="text-[10px] text-purple-600 font-medium">
                          ✨ With AEO Content
                        </div>
                        <div className="flex items-center gap-2 text-[10px]">
                          <span className="text-blue-400 line-through">
                            {((q.originalDistance ?? 0) * 100).toFixed(0)}%
                          </span>
                          <span className="text-purple-600 font-medium">
                            → {((q.aeoDistance ?? 0) * 100).toFixed(0)}%
                          </span>
                          <span className="text-green-600">
                            ({(((q.originalDistance ?? 0) - (q.aeoDistance ?? 0)) * 100).toFixed(0)}% closer)
                          </span>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </TooltipProvider>

      {summary && (
        <div className="space-y-4">
          {/* Overall Coverage Score (prominent) */}
          {summary.coveredPercent !== undefined && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-blue-600">
                    {summary.coveredPercent}%
                  </div>
                  <div className="text-sm text-blue-600/80">Question Coverage</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {summary.answered} answered + {summary.partial} partial
                  </div>
                </div>
                {summary.searchVolume && summary.searchVolume.coveredPercent !== undefined && (
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">
                      {summary.searchVolume.coveredPercent}%
                    </div>
                    <div className="text-sm text-purple-600/80">Volume Coverage</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {(summary.searchVolume.answered + summary.searchVolume.partial).toLocaleString()}/mo
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-600">
                {summary.answeredPercent}%
              </div>
              <div className="text-sm text-green-600">Fully Answered</div>
              {summary.searchVolume && summary.searchVolume.answered > 0 && (
                <div className="text-xs text-green-500 mt-1">
                  {summary.searchVolume.answered.toLocaleString()}/mo
                </div>
              )}
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-3">
              <div className="text-2xl font-bold text-yellow-600">
                {summary.partialPercent}%
              </div>
              <div className="text-sm text-yellow-600">Can Answer</div>
              {summary.searchVolume && summary.searchVolume.partial > 0 && (
                <div className="text-xs text-yellow-500 mt-1">
                  {summary.searchVolume.partial.toLocaleString()}/mo
                </div>
              )}
            </div>
            <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-3">
              <div className="text-2xl font-bold text-red-600">
                {summary.gapPercent}%
              </div>
              <div className="text-sm text-red-600">Content Gaps</div>
              {summary.searchVolume && summary.searchVolume.gap > 0 && (
                <div className="text-xs text-red-500 mt-1">
                  {summary.searchVolume.gap.toLocaleString()}/mo
                </div>
              )}
            </div>
          </div>
          {summary.searchVolume && summary.searchVolume.total > 0 && (
            <div className="text-center text-sm text-muted-foreground">
              Total category search volume: <span className="font-semibold">{summary.searchVolume.total.toLocaleString()}</span>/month
              {summary.searchVolume.answeredPercent > 0 && (
                <span className="ml-2">
                  • <span className="text-green-600 font-medium">{summary.searchVolume.answeredPercent}%</span> captured
                </span>
              )}
            </div>
          )}

          {/* AEO Improvement Summary */}
          {summary.aeoImprovement && summary.aeoImprovement.improvedCount > 0 && (
            <div className="bg-purple-50 dark:bg-purple-950/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-purple-600 text-lg">✨</span>
                <span className="font-semibold text-purple-700 dark:text-purple-300">AEO Content Impact</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-xl font-bold text-purple-600">
                    {summary.aeoImprovement.improvedCount}
                  </div>
                  <div className="text-xs text-purple-600/70">
                    Questions Improved
                  </div>
                </div>
                <div>
                  <div className="text-xl font-bold text-purple-600">
                    +{summary.aeoImprovement.avgImprovement.toFixed(1)}%
                  </div>
                  <div className="text-xs text-purple-600/70">
                    Avg. Closer
                  </div>
                </div>
                <div>
                  <div className="text-xl font-bold text-purple-600">
                    +{summary.aeoImprovement.avgWeightedImprovement.toFixed(1)}%
                  </div>
                  <div className="text-xs text-purple-600/70">
                    Weighted by Volume
                  </div>
                </div>
                <div>
                  <div className="text-xl font-bold text-purple-600">
                    {summary.aeoImprovement.totalSearchVolumeImproved.toLocaleString()}
                  </div>
                  <div className="text-xs text-purple-600/70">
                    Searches/mo Improved
                  </div>
                </div>
              </div>
              <div className="mt-3 text-xs text-center text-purple-600/60">
                AEO-generated FAQ content brings your product closer to {summary.aeoImprovement.improvedPercent}% of category questions
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
